import React, { Children } from 'react'
import { Box } from '@mui/material'
import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'

import { poolwerxColors } from '../../theme/colors'
import { headingIconFor } from './releaseHeadingIcons'

interface ReleaseBodyProps {
  body: string
  dense?: boolean
  /** Modal / history FP body: render `##` headings with an icon chip + divider. */
  decorateHeadings?: boolean
}

/** Flatten markdown heading children to a plain string for the icon lookup. */
function headingText(children: React.ReactNode): string {
  return Children.toArray(children)
    .map((child) => (typeof child === 'string' ? child : ''))
    .join('')
    .trim()
}

const DecoratedH2: React.FC<{ children: React.ReactNode; color: string }> = ({
  children,
  color,
}) => {
  const Icon = headingIconFor(headingText(children))
  return (
    <Box component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          borderRadius: '6px',
          bgcolor: poolwerxColors.tealLight,
          color,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 14 }} />
      </Box>
      <Box component="span" sx={{ flexShrink: 0 }}>
        {children}
      </Box>
      <Box
        component="span"
        aria-hidden
        sx={{
          flex: 1,
          height: '1px',
          bgcolor: poolwerxColors.grey[300],
          ml: 0.5,
        }}
      />
    </Box>
  )
}

export const ReleaseBody: React.FC<ReleaseBodyProps> = ({
  body,
  dense = false,
  decorateHeadings = false,
}) => {
  const headingColor = dense ? poolwerxColors.grey[700] : poolwerxColors.teal
  const components: Components | undefined = decorateHeadings
    ? {
        h2: ({ children }) => (
          <DecoratedH2 color={headingColor}>{children}</DecoratedH2>
        ),
      }
    : undefined
  return (
    <Box
      sx={{
        minWidth: 0,
        width: '100%',
        '& h1, & h2, & h3': { mt: 2, mb: 1 },
        '& h2': {
          fontSize: dense ? '0.85rem' : '0.95rem',
          fontWeight: 700,
          color: headingColor,
          mt: 2.25,
          mb: 0.75,
        },
        '& h2:first-of-type, & h3:first-of-type': { mt: 0.5 },
        '& h3': {
          fontSize: dense ? '0.82rem' : '0.9rem',
          fontWeight: 700,
          color: headingColor,
        },
        '& p': { my: 1, fontSize: '0.875rem' },
        '& ul': { listStyle: 'none', pl: 0, my: 1 },
        '& li': {
          position: 'relative',
          pl: 2.25,
          mb: dense ? 0.75 : 1.1,
          fontSize: '0.875rem',
          lineHeight: 1.5,
        },
        '& li::before': {
          content: '"•"',
          position: 'absolute',
          left: 6,
          top: 0,
          color: poolwerxColors.teal,
          fontWeight: 700,
        },
        '& li > strong:first-of-type, & strong': {
          fontWeight: 700,
          color: poolwerxColors.charcoal,
        },
        '& a': { wordBreak: 'break-word' },
        '& pre': {
          maxWidth: '100%',
          overflowX: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        },
        '& code': {
          fontSize: '0.85em',
          px: 0.5,
          py: 0.1,
          bgcolor: 'rgba(0,0,0,0.06)',
          borderRadius: 0.5,
          wordBreak: 'break-word',
        },
      }}
    >
      <ReactMarkdown components={components}>{body}</ReactMarkdown>
    </Box>
  )
}

export default ReleaseBody
